import React from "react";
import { useContentShareState } from "amazon-chime-sdk-component-library-react";
import { MeetingInfoContext } from "../context";
import { ConnectIoTContext } from "../websocket/connect-iot";
import { MessageType } from "../components/live-annotator/types/annotation-types";

interface UseLiveAnnotationProps {
  expired?: boolean;
  urlIndex?: number;
  disabled?: boolean;
  liveAnnotationChanged?: (showAnnotation: boolean) => void;
}

export const useLiveAnnotation = ({
  expired = false,
  urlIndex,
  disabled,
  liveAnnotationChanged,
}: UseLiveAnnotationProps) => {

  const { tiles } = useContentShareState();

  const contentShareIsActive = tiles.length > 0

  const {
    connected,
    canAnnotate,
    channelName,
    setAnnotationUrlIndex,
    showAnnotation,
    setShowAnnotation
  } = React.useContext(MeetingInfoContext);

  const { sendMessage } = React.useContext(ConnectIoTContext);

  const onClick = () => {
    if (showAnnotation) {
      updateUrl(0, false);
      return;
    }

    if (urlIndex === null || urlIndex === undefined) {
      return;
    }

    updateUrl(urlIndex, true);
  };

  const updateUrl = (url: number, active: boolean) => {
    setShowAnnotation(active);
    setAnnotationUrlIndex(url);

    const message = {
      type: MessageType.Active,
      active,
      url: urlIndex,
    };

    sendMessage(channelName, JSON.stringify(message));

    if (liveAnnotationChanged) {
      liveAnnotationChanged(active);
    }
  };

  const getTooltip = () => {
    if (contentShareIsActive) {
      return "Cannot start live annotation during content share";
    }

    if (!connected) {
      return "Live annotation disabled. Not connected to annotation server";
    }

    if (expired) {
      return "Live annotation disabled. Annotation URL has expired";
    }

    if (urlIndex === null) {
      return "Live annotation disabled. No image selected for annotation";
    }

    if (!canAnnotate) {
      return "Live annotation disabled. Not authorized to annotate";
    }

    if (showAnnotation) {
      return "Stop Live Annotation";
    }

    return "Start Live Annotation";
  };

  const isDisabled = Boolean(
    disabled ||
    !connected ||
    (urlIndex === null && !showAnnotation) ||
    expired ||
    !canAnnotate ||
    contentShareIsActive
  );

  const tooltipText = getTooltip();
  const label = showAnnotation ? "Stop Live Annotation" : "Start Live Annotation";
  const color = showAnnotation ? ("error" as const) : ("primary" as const);

  return {
    showAnnotation,
    onClick,
    isDisabled,
    tooltipText,
    label,
    color,
  };
};
